/* @flow */

import React from 'react';

import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';

import PhoneNumberComponent from 'components/formFields/phoneNumber';
import ButtonComponent from 'components/buttons/button';
import FormErrorsComponent from 'components/formErrors';
import IconCancelOutlined from 'components/icons/outlined/cancel';
import IntlMessageComponent from 'components/formatters/intlMessage';
import LanguageFieldComponent from 'components/formFields/types/language';
import TitleFieldComponent from 'components/formFields/types/title';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';

import CompanyContactPerson from 'models/companies/companyContactPerson';
import SupplierContactPersonForm from './model';

type SupplierContactPersonFormViewComponentProps = {
  errors: Map<string,?string>;
  form: SupplierContactPersonForm;
  formErrors: string[];
  isProcessing: boolean;
  cancel: () => void;
  handleChange: () => void;
  handleTypeChange: (fieldName: string) => (id: number) => void;
  submitForm: () => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.lightgrey.main,
    marginTop: '2em',
    border: `1px solid ${theme.palette.lightgrey.dark}`
  },
  icon: {
    color: theme.palette.grey.main
  }
}));

export default function SupplierContactPersonFormViewComponent(props: SupplierContactPersonFormViewComponentProps) {
  const classes = useStyles();

  return (
    <Paper elevation={0}>

      <Box mt={5} p={3}>
        <PageSubTitleViewComponent id="suppliercontactperson.form.title" />

        <FormErrorsComponent formErrors={props.formErrors} />

        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6}>
            <Box pr={4}>
              <TitleFieldComponent
                label={<IntlMessageComponent id="form.title.label" />}
                error={!!props.errors.get('titleId')}
                helperText={(props.errors.get('titleId')) ?
                  <IntlMessageComponent id={props.errors.get('titleId')} /> : ''}
                value={props.form.titleId}
                changeHandler={props.handleTypeChange('titleId')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6}>
            <Box pr={4}>
              <TextField
                id="form.lastName"
                label={<IntlMessageComponent id="form.name.label" />}
                error={!!props.errors.get('lastName')}
                helperText={(props.errors.get('lastName')) ?
                  <IntlMessageComponent id={props.errors.get('lastName')} /> : ''}
                value={props.form.lastName}
                onChange={props.handleChange('lastName')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box pl={4}>
              <TextField
                id="form.firstName"
                label={<IntlMessageComponent id="form.firstname.label" />}
                error={!!props.errors.get('firstName')}
                helperText={(props.errors.get('firstName')) ?
                  <IntlMessageComponent id={props.errors.get('firstName')} /> : ''}
                value={props.form.firstName}
                onChange={props.handleChange('firstName')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6}>
            <Box pr={4}>
              <TextField
                id="form.email"
                data-walkme="sellinvoice-field-email"
                label={<IntlMessageComponent id="page.sellInvoice.tab3.email.label" />}
                error={!!props.errors.get('email')}
                helperText={(props.errors.get('email')) ?
                  <IntlMessageComponent id={props.errors.get('email')} /> : ''}
                value={props.form.email}
                onChange={props.handleChange('email')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box pl={4}>
              <LanguageFieldComponent
                label={<IntlMessageComponent id="form.language.label" />}
                error={!!props.errors.get('languageId')}
                helperText={(props.errors.get('languageId')) ?
                  <IntlMessageComponent id={props.errors.get('languageId')} /> : ''}
                value={props.form.languageId}
                changeHandler={props.handleTypeChange('languageId')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6}>
            <Box pr={4}>
              <TextField
                id="form.functionName"
                data-walkme="sellinvoice-field-fonction"
                label={<IntlMessageComponent id="page.sellInvoice.tab3.function.label" />}
                error={!!props.errors.get('functionName')}
                helperText={(props.errors.get('functionName')) ?
                  <IntlMessageComponent id={props.errors.get('functionName')} /> : ''}
                value={props.form.functionName}
                onChange={props.handleChange('functionName')}
                fullWidth
                margin="normal"
                required
              />
            </Box>
          </Grid>
        </Grid>
        <Box py={2}>
          <ButtonComponent
            color="gray"
            variant="contained"
            onClick={props.cancel}
          ><IntlMessageComponent id="general.cancel" /></ButtonComponent>
          <Box component="span" ml={1}>
            <ButtonComponent
              color="primary"
              variant="contained"
              isProcessing={props.isProcessing}
              onClick={props.submitForm}
            ><IntlMessageComponent id="general.submit" /></ButtonComponent>
          </Box>
        </Box>
      </Box>
    </Paper >
  );
}
